'use client';

import { isBooleanValue, isNumberValue, isStringValue } from '../lib/validation/primitives';


import { Children, isValidElement, type ComponentProps, type ReactNode } from 'react';
import { Streamdown, type Components } from 'streamdown';

import { YouTubePreview } from '@/components/youtube-preview';
import { youtubeIdFromUrl } from '@/lib/content/prepare-blog-markdown';
import { cn } from '@/lib/utils';

/**
 * Canonical markdown renderer for in-app document viewing. Same prose styling
 * as the CMO knowledge-base drawer (see [[knowledge-file-drawer]]) so every
 * viewer in the app looks identical: headings, GFM tables, blockquotes, code,
 * lists, links — all themed against our dark surface.
 *
 * Used by:
 *   - admin CMO knowledge-base file drawer
 *   - dashboard crawled-document viewer (/dashboard/folders/crawled/[id])
 *
 * If you need to tweak the prose style, do it here once — every consumer
 * inherits it.
 *
 * Deliberately does NOT pass `linkSafety={{ enabled: false }}` the way
 * {@link ArticleMarkdown} below does — both viewers render externally-sourced
 * content (a crawled document, an ingested knowledge file), so Streamdown's
 * default click-confirm guard on links is a real safety feature here, not
 * dead weight. Only disable it for content this app authored/generated
 * itself.
 */
const MARKDOWN_PROSE =
  'text-[14px] leading-7 text-ink ' +
  '[&_a]:text-violet-300 [&_a]:underline [&_a]:underline-offset-2 ' +
  '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-line-strong [&_blockquote]:pl-4 [&_blockquote]:text-ink-2 [&_blockquote]:italic ' +
  '[&_code]:rounded [&_code]:bg-surface-2 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[12.5px] [&_code]:text-violet-200 ' +
  '[&_em]:italic [&_em]:text-ink-2 ' +
  '[&_h1]:mb-4 [&_h1]:mt-0 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:tracking-tight [&_h1]:text-ink ' +
  '[&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:border-b [&_h2]:border-line [&_h2]:pb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-ink ' +
  '[&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:text-ink ' +
  '[&_hr]:my-7 [&_hr]:border-line ' +
  '[&_li]:my-1 [&_li]:marker:text-ink-3 ' +
  '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 ' +
  '[&_p]:my-3 ' +
  '[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-line [&_pre]:bg-black/40 [&_pre]:p-4 ' +
  '[&_strong]:font-semibold [&_strong]:text-ink ' +
  '[&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[13px] ' +
  '[&_td]:border-b [&_td]:border-line [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:text-ink-2 ' +
  '[&_th]:border-b [&_th]:border-line-strong [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-[11px] [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-ink-3 ' +
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5';

export function MarkdownView({
  content,
  className,
}: {
  content: string;
  /** Extra classes appended after the canonical prose styling (use sparingly). */
  className?: string;
}) {
  return <Streamdown className={cn(MARKDOWN_PROSE, className)}>{content}</Streamdown>;
}

/**
 * Public marketing-page prose (pSEO comparison/alternatives bodies, etc.). Same
 * Streamdown engine as {@link MarkdownView} — so GFM tables and images render —
 * but themed against the public CSS-var palette at article-readable sizes,
 * instead of the 14px admin-drawer styling above. Use this for any public,
 * SEO-facing body; use MARKDOWN_PROSE for in-app/admin viewers.
 */
import { ARTICLE_PROSE } from '@/components/prose';

/**
 * Body-level "# Heading" renders as <h2>, not <h1>: PseoPage already emits the
 * single page <h1> from the title, and the generated body repeats that title as
 * its own leading "# " line, so every page shipped two identical h1s.
 *
 * renderMarkdownContent (src/lib/content/markdown.tsx) has demoted body h1s for
 * exactly this reason since the Bing Site Scan "More than one h1 tag" finding.
 * Moving pSEO bodies onto Streamdown for GFM tables (5ac175d) left that rule
 * behind and reintroduced the defect on ~171 pSEO entities; this restores it.
 *
 * Done as a component override rather than a string preprocess so a "# " inside
 * a fenced code block or table cell is untouched — only real headings demote.
 */
/** Plain text of a heading's children, for slugging. */
function nodeText(node: ReactNode): string {
  if (node == null || isBooleanValue(node)) return '';

  if (isStringValue(node) || isNumberValue(node)) return String(node);

  if (Array.isArray(node)) return node.map(nodeText).join('');

  if (isValidElement<{ children?: ReactNode }>(node)) return nodeText(node.props.children);

  return '';
}

/**
 * GitHub-style heading slug: lowercase, strip punctuation, spaces to hyphens.
 * Gives every body heading a stable id so in-page anchors (quick-pick table
 * rows, TOC links, "#faq") resolve. Mirrors github-slugger's rules closely
 * enough for ASCII headings; keep in sync with the anchor slugs authored in
 * content/pseo-programs/*.json.
 */
function headingId(children: ReactNode): string | undefined {
  const text = nodeText(children).trim().toLowerCase();

  if (!text) return undefined;

  const slug = text.replace(/[^\p{L}\p{N}\s-]/gu, '').replace(/\s+/g, '-');

  return slug || undefined;
}

function youtubeThumb(children: ReactNode): { title: string } | null {
  for (const child of Children.toArray(children)) {
    if (!isValidElement<{ src?: string; alt?: string }>(child)) continue;

    const src = child.props.src ?? '';

    if (!/i\.ytimg\.com|img\.youtube\.com/.test(src)) continue;

    const title = child.props.alt?.trim();

    return { title: title || 'Watch on YouTube' };
  }

  return null;
}

function ArticleLink({
  href,
  children,
  node: _node,
  ...props
}: ComponentProps<'a'> & { node?: unknown }) {
  const url = isStringValue(href) ? href : '';
  const videoId = youtubeIdFromUrl(url);
  const thumb = videoId ? youtubeThumb(children) : null;

  if (videoId && thumb) {
    return <YouTubePreview videoId={videoId} title={thumb.title} href={url} />;
  }

  return (
    <a href={href} {...props}>
      {children}
    </a>
  );
}

const ARTICLE_COMPONENTS: Components = {
  h1: ({ children, ...props }) => (
    <h2 {...props} id={headingId(children)}>
      {children}
    </h2>
  ),
  h2: ({ children, ...props }) => (
    <h2 {...props} id={headingId(children)}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 {...props} id={headingId(children)}>
      {children}
    </h3>
  ),
};

export function ArticleMarkdown({
  content,
  className,
  youtubePreviews = false,
}: {
  content: string;
  className?: string;
  /** Standalone YouTube thumbnail links render as feed cards. */
  youtubePreviews?: boolean;
}) {
  const components: Components = { ...ARTICLE_COMPONENTS };

  if (youtubePreviews) components.a = ArticleLink;

  return (
    <Streamdown
      className={cn(ARTICLE_PROSE, className)}
      components={components}
      // Streamdown's linkSafety defaults to {enabled: true} (see MarkdownView
      // below, which keeps that default on purpose). When enabled, every link
      // renders as a <button onClick> with NO href attribute in the DOM at
      // all — not a click-guarded <a>, an anchor-less button. A crawler can't
      // discover a link that was never emitted, so every internal link inside
      // every pSEO/blog article body was invisible to Googlebot: 0 internal
      // link equity flowing through content bodies, sitewide. This content is
      // ours (pipeline-generated, editorially gated, not third-party/crawled),
      // so the untrusted-link protection MarkdownView needs doesn't apply
      // here — disable it so links render as real <a href> tags.
      linkSafety={{ enabled: false }}
    >
      {content}
    </Streamdown>
  );
}
